import React, { useCallback, useEffect, useState } from 'react';
import { Box, Stack, Typography, Chip, TextField, IconButton, Tooltip, Avatar, Collapse, InputBase } from '@mui/material';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import http, { apiErrorMessage } from '../../Api/http';
import { GetAuditLog } from '../../Api/Api';
import { Panel, EmptyState, SkeletonRows, field, card } from '../uiKit';
import { avatarTone, initialsFromName as initials } from '../../utils/format';

const todayISO = () => new Date().toISOString().slice(0, 10);
const toDMY = (iso) => {
    if (!iso) return '';
    const [y, m, d] = String(iso).split('-');
    return d ? `${d}-${m}-${y}` : '';
};

// Read-only "who did what today" feed. The server already scopes entries to
// whichever of Vacancies/Candidates/Interviews the caller holds — a role
// with just one of those modules still gets a 200 with only its own slice,
// so this tab never assumes actionCounts/entries reflect everything.
export default function ActivityTab() {
    const [date, setDate] = useState(todayISO());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [openId, setOpenId] = useState(null);
    const [q, setQ] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: body } = await http.get(GetAuditLog, { params: { date: toDMY(date) } });
            if (body?.error) throw new Error(body.message || 'Could not load the activity log.');
            setData(body?.data || null);
            setLoadError('');
        } catch (err) {
            setData(null);
            setLoadError(apiErrorMessage(err, 'Could not load the activity log.'));
        } finally {
            setLoading(false);
        }
    }, [date]);

    useEffect(() => { load(); }, [load]);

    const counts = Array.isArray(data?.actionCounts) ? data.actionCounts : [];
    const allEntries = Array.isArray(data?.entries) ? data.entries : [];
    const entries = q.trim()
        ? allEntries.filter((e) => [e.actorName, e.actor, e.summary].some((x) => String(x || '').toLowerCase().includes(q.trim().toLowerCase())))
        : allEntries;

    return (
        <Box sx={{ p: 2, pt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Stack direction="row" sx={{ ...card, alignItems: 'center', gap: 1.2, flexWrap: 'wrap', p: 1.6 }}>
                <TextField
                    type="date" size="small" label="Date" value={date} onChange={(e) => setDate(e.target.value)}
                    sx={{ ...field, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 42, bgcolor: '#fff' } }}
                    slotProps={{ htmlInput: { max: todayISO() }, inputLabel: { shrink: true } }}
                />
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '7px', px: 1.4, height: 42, width: { xs: '100%', sm: 300 } }}>
                    <SearchRoundedIcon sx={{ fontSize: 18, color: '#98A0AE' }} />
                    <InputBase placeholder="Search user…" value={q} onChange={(e) => setQ(e.target.value)} sx={{ fontSize: 13, flex: 1 }} />
                </Stack>
                <Tooltip arrow title="Reload">
                    <span>
                        <IconButton onClick={load} disabled={loading} sx={{ border: '1px solid #E6EAF1', borderRadius: '7px', bgcolor: '#fff', color: '#64748B', height: 42, width: 42, '&:hover': { bgcolor: '#E4F0F9', color: '#135F93' } }}>
                            <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>

            <Panel
                title="Activity"
                icon={HistoryRoundedIcon}
                chip={data ? `${data.totalActions ?? allEntries.length} action${(data.totalActions ?? allEntries.length) === 1 ? '' : 's'}` : undefined}
            >
                {loading ? (
                    <SkeletonRows count={5} />
                ) : loadError ? (
                    <EmptyState icon={HistoryRoundedIcon} title="Couldn't load the activity log" hint={loadError} />
                ) : entries.length === 0 ? (
                    <EmptyState icon={HistoryRoundedIcon} title="Nothing happened on this day" hint="Vacancy, candidate and interview actions you're allowed to see will show up here." />
                ) : (
                    <>
                        {counts.length > 0 && (
                            <Stack direction="row" spacing={0.8} sx={{ flexWrap: 'wrap', gap: 0.8, mb: 2 }}>
                                {counts.map((c) => (
                                    <Chip key={c.action} label={`${c.count} ${c.label}`} size="small" sx={{ height: 24, fontSize: 11.5, fontWeight: 700, bgcolor: '#E4F0F9', color: '#135F93' }} />
                                ))}
                            </Stack>
                        )}
                        <Stack spacing={0.8}>
                            {entries.map((e) => {
                                const open = openId === e.id;
                                const hasDetails = e.details && Object.keys(e.details).length > 0;
                                return (
                                    <Box key={e.id} sx={{ border: '1px solid #F3F8FC', borderRadius: '9px', overflow: 'hidden' }}>
                                        <Stack
                                            direction="row" spacing={1.3}
                                            onClick={() => hasDetails && setOpenId(open ? null : e.id)}
                                            sx={{ alignItems: 'center', p: 1.3, cursor: hasDetails ? 'pointer' : 'default', '&:hover': hasDetails ? { bgcolor: '#FAFAFD' } : undefined }}
                                        >
                                            <Avatar sx={{ width: 32, height: 32, ...avatarTone(e.actorName || e.actor || ''), fontSize: 11.5, fontWeight: 700, flexShrink: 0 }}>
                                                {initials(e.actorName || e.actor || '?')}
                                            </Avatar>
                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Typography sx={{ fontSize: 13, color: '#0F172A' }}>
                                                    <b>{e.actorName || e.actor}</b> {e.summary}
                                                </Typography>
                                                <Typography sx={{ fontSize: 11, color: '#98A0AE' }}>{e.time}</Typography>
                                            </Box>
                                            {hasDetails && <ExpandMoreRoundedIcon sx={{ fontSize: 18, color: '#98A0AE', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s', flexShrink: 0 }} />}
                                        </Stack>
                                        {hasDetails && (
                                            <Collapse in={open}>
                                                <Box sx={{ px: 1.6, py: 1.2, borderTop: '1px solid #F3F8FC', bgcolor: '#FAFAFD' }}>
                                                    {Object.entries(e.details).map(([k, v]) => (
                                                        <Stack key={k} direction="row" sx={{ justifyContent: 'space-between', py: 0.4, gap: 1.5 }}>
                                                            <Typography sx={{ fontSize: 11, color: '#98A0AE', textTransform: 'capitalize', flexShrink: 0 }}>{k.replace(/([A-Z])/g, ' $1').trim()}</Typography>
                                                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#334155', textAlign: 'right' }}>{String(v)}</Typography>
                                                        </Stack>
                                                    ))}
                                                </Box>
                                            </Collapse>
                                        )}
                                    </Box>
                                );
                            })}
                        </Stack>
                    </>
                )}
            </Panel>
        </Box>
    );
}
